import { useEffect, useMemo, useState } from 'react'
import AdminShell from './AdminShell'
import {
  AdminButton,
  AdminCard,
  AdminSkeleton,
  AdminStatePanel,
} from './AdminUi'

type SessionPayload = {
  authenticated: true
  user: { email: string; role: string }
  csrfToken: string
}

function environmentFromHostname(hostname: string) {
  const host = hostname.toLowerCase()
  if (host.includes('staging')) return 'Staging'
  if (host === 'localhost' || host === '127.0.0.1') return 'Local'
  return undefined
}

export default function AdminNotFoundPage() {
  const [session, setSession] = useState<SessionPayload | null>(null)
  const [error, setError] = useState('')
  const [logoutBusy, setLogoutBusy] = useState(false)
  const environmentLabel = useMemo(() => environmentFromHostname(window.location.hostname), [])
  const requestedPath = useMemo(() => window.location.pathname, [])

  useEffect(() => {
    document.title = 'Page not found | Sugar Gremlin Boss Zone'

    void fetch('/api/admin/session', {
      credentials: 'same-origin',
      cache: 'no-store',
    })
      .then(async (response) => {
        if (response.status === 401) {
          const next = encodeURIComponent(requestedPath)
          window.location.replace(`/admin/login?next=${next}`)
          return
        }
        if (!response.ok) throw new Error('Boss Zone session could not be loaded.')
        setSession(await response.json() as SessionPayload)
      })
      .catch(() => setError('Boss Zone security could not be verified.'))
  }, [requestedPath])

  async function logout() {
    if (!session || logoutBusy) return
    setLogoutBusy(true)
    setError('')
    try {
      const response = await fetch('/api/admin/logout', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': session.csrfToken },
      })
      if (!response.ok) throw new Error('logout failed')
      window.location.replace('/admin/login')
    } catch {
      setError('Logout could not be completed. Please try again.')
      setLogoutBusy(false)
    }
  }

  if (error && !session) {
    return (
      <main className="admin-shell-gate-error">
        <AdminStatePanel
          state="error"
          title="Boss Zone unavailable"
          description={error}
          action={<AdminButton type="button" onClick={() => window.location.reload()}>Retry</AdminButton>}
        />
      </main>
    )
  }

  if (!session) {
    return (
      <main className="admin-shell-gate-loading" aria-label="Checking Boss Zone session">
        <AdminCard>
          <AdminSkeleton lines={5} />
        </AdminCard>
      </main>
    )
  }

  return (
    <AdminShell
      title="Page not found"
      eyebrow="BOSS ZONE · 404"
      description="That Boss Zone page does not exist or may have moved."
      user={session.user}
      onLogout={() => void logout()}
      logoutBusy={logoutBusy}
      environmentLabel={environmentLabel}
      suppressActiveNav
    >
      {error && (
        <div className="admin-shell-qa__error" role="alert">
          {error}
        </div>
      )}

      <AdminCard>
        <AdminStatePanel
          state="empty"
          title="Nothing here"
          description={`No Boss Zone page exists at ${requestedPath}. Use the navigation to choose another section, or return to the Dashboard.`}
          action={(
            <AdminButton type="button" onClick={() => window.location.assign('/admin')}>
              Back to Dashboard
            </AdminButton>
          )}
        />
      </AdminCard>
    </AdminShell>
  )
}
